import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="privacy-policy-page" className="pt-24 lg:pt-28 pb-20 bg-white min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-2 text-compliance-green font-body text-sm font-medium hover:underline mb-8">
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="mb-10">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-ink mb-3">Privacy Policy</h1>
          <p className="font-body text-sm text-slate-text">Effective date: 15 April 2026 &middot; Last updated: 15 April 2026</p>
        </div>

        <div className="legal-content">
          <p>This Privacy Policy explains how [Full legal company name] collects, uses, stores, shares, and protects personal data when you use the ActCheck mobile app, the ActCheck website at actcheck.co.uk, and any related services.</p>
          <p>The ICO says privacy notices must clearly tell people what information is collected, why it is used, the lawful basis, who it is shared with, how long it is kept, and what rights people have. This policy is structured on that basis.</p>

          <h2>1. Who we are</h2>
          <ul>
            <li><strong>Data controller:</strong> [Full legal company name]</li>
            <li><strong>Registered office:</strong> [Registered address]</li>
            <li><strong>Company number:</strong> [Company number]</li>
            <li><strong>Email:</strong> <a href="mailto:privacy@actcheck.co.uk">privacy@actcheck.co.uk</a></li>
            <li><strong>Website:</strong> <a href="https://actcheck.co.uk">https://actcheck.co.uk</a></li>
          </ul>
          <p>If we are required to appoint a Data Protection Officer and have done so, their contact details are: [DPO name/email].</p>

          <h2>2. Scope of this Privacy Policy</h2>
          <p>This Privacy Policy applies to personal data collected through:</p>
          <ul>
            <li>the ActCheck mobile app;</li>
            <li>the website actcheck.co.uk;</li>
            <li>communications with users;</li>
            <li>support requests;</li>
            <li>onboarding, billing, and account management;</li>
            <li>uploaded documents and records;</li>
            <li>reminders and workflow messages; and</li>
            <li>related services we provide.</li>
          </ul>

          <h2>3. The personal data we collect</h2>
          <p>Depending on how you use ActCheck, we may collect:</p>

          <h3>A. Identity and account data</h3>
          <ul>
            <li>name, email address, phone number;</li>
            <li>login credentials or SSO identifiers;</li>
            <li>account type, role, and profile information;</li>
            <li>language and accessibility preferences.</li>
          </ul>

          <h3>B. Property, tenancy, and workflow data</h3>
          <ul>
            <li>property addresses and tenancy dates;</li>
            <li>landlord and tenant names;</li>
            <li>participant and household details;</li>
            <li>tenancy status, deposit, rent, notice, rights-pack, repair, maintenance, and checklist information;</li>
            <li>records of invites, acknowledgements, and shared access permissions.</li>
          </ul>

          <h3>C. Documents and evidence</h3>
          <ul>
            <li>tenancy agreements, certificates, notices;</li>
            <li>photos, videos, scans, PDFs, invoices, receipts, letters, and uploaded messages;</li>
            <li>generated exports and timelines;</li>
            <li>accessible PDFs we create on your behalf.</li>
          </ul>

          <h3>D. Communications and support data</h3>
          <ul>
            <li>messages sent through the Platform;</li>
            <li>email delivery information and push notification status;</li>
            <li>support tickets, help requests, and complaint records where applicable.</li>
          </ul>

          <h3>E. Device, usage, and technical data</h3>
          <ul>
            <li>IP address, device identifiers, browser type, operating system;</li>
            <li>app version, crash logs, analytics events;</li>
            <li>session activity and cookie data.</li>
          </ul>

          <h3>F. Billing and transaction data</h3>
          <ul>
            <li>subscription status, invoices, payment status;</li>
            <li>order history, add-on purchases, and provider transaction references.</li>
          </ul>
          <p>We do not usually store full payment card details ourselves, where a third-party payment processor handles them.</p>

          <h3>G. Sensitive or special category data</h3>
          <p>Some user uploads may contain sensitive information, such as health information mentioned in repair complaints, disability-related information, or information contained in tenancy or evidence documents.</p>
          <p>Please avoid uploading sensitive information unless it is genuinely necessary for your use of the Platform.</p>

          <h2>4. How we collect personal data</h2>
          <p>We collect personal data:</p>
          <ul>
            <li>directly from you when you sign up, fill in forms, upload files, send messages, or buy services;</li>
            <li>from other users who invite you to a tenancy or shared workflow;</li>
            <li>from service providers such as payment, email, messaging, identity, hosting, analytics, AI, or postal fulfilment providers;</li>
            <li>automatically through the app, website, device, and cookies or similar technologies; and</li>
            <li>from your use of workflows, reminders, exports, and support services.</li>
          </ul>

          <h2>5. Why we use personal data and our lawful bases</h2>
          <p>The ICO explains that you must identify a lawful basis for each type of processing, and common bases include contract, consent, and legitimate interests. We generally use the following lawful bases:</p>

          <h3>A. Performance of a contract</h3>
          <p>We use personal data where necessary to provide the Platform and your requested services, including creating and managing your account, linking users to properties and tenancies, storing and displaying documents and records, running checklists, reminders, exports, and workflows, processing subscriptions and add-ons, sending operational emails or messages, and providing customer support.</p>

          <h3>B. Legitimate interests</h3>
          <p>We may use personal data where it is necessary for our legitimate interests, provided those interests are not overridden by your rights and freedoms. This may include improving the Platform, product analytics, fraud prevention, information security, abuse detection, audit logging, platform reliability and debugging, service monitoring, internal reporting, and limited operational messaging.</p>

          <h3>C. Consent</h3>
          <p>We rely on consent where required, for example certain marketing communications, certain cookies or similar technology uses, WhatsApp or similar messaging opt-ins, optional notifications, and some uses of sensitive information where consent is the most appropriate basis.</p>
          <p>You can withdraw consent at any time, but this does not affect lawful processing carried out before withdrawal.</p>

          <h3>D. Legal obligation</h3>
          <p>We may use and retain personal data where necessary to comply with legal obligations, regulatory requirements, lawful requests, fraud prevention obligations, tax/accounting obligations, or to respond to legal claims.</p>

          <h3>E. Legal claims</h3>
          <p>Where necessary, we may use personal data for the establishment, exercise, or defence of legal claims.</p>

          <h2>6. Special category data</h2>
          <p>Where you choose to upload or send sensitive data, such as health-related information in a repair issue, we will process it only where we have an appropriate legal basis and condition under applicable data protection law, such as explicit consent, legal claims, or another lawful condition that applies in context.</p>
          <p>If you do not need to upload special category data, do not include it.</p>

          <h2>7. AI and automated processing</h2>
          <p>ActCheck may use AI-assisted tools for OCR, document classification, extraction of dates, fields, and parties, summaries, risk flags, prioritised reminders, plain-English guidance, and export preparation.</p>
          <p>These features are designed to assist users, not replace professional advice or make fully automated legal decisions.</p>
          <p>We may store prompts, uploaded source material, extracted data, confidence scores, model version, prompt version, policy version, content version, and audit records of material AI outputs. We use this information to provide the feature, maintain safety controls, improve quality, investigate errors, and keep an audit trail.</p>
          <p>You should review AI outputs before relying on them.</p>

          <h2>8. Cookies and similar technologies</h2>
          <p>We use cookies and similar technologies on the website and, where relevant, equivalent technologies in the app for essential service functionality, security, login/session continuity, performance and analytics, and optional preferences or communications features.</p>
          <p>Under PECR and ICO guidance, we must provide clear information about the storage and access technologies we use, and non-essential technologies generally require consent.</p>

          <h2>9. Direct marketing</h2>
          <p>Where we send marketing communications, we will do so in line with applicable data protection and PECR rules. You can opt out at any time using the unsubscribe method in the message or by changing your account preferences.</p>
          <p>Service messages about your account, billing, reminders, security, tenancy workflows, and operational events are not the same as marketing and may still be sent where necessary.</p>

          <h2>10. Who we share personal data with</h2>
          <p>We may share personal data with:</p>
          <ul>
            <li>hosting and cloud infrastructure providers;</li>
            <li>email, push notification, and messaging providers;</li>
            <li>payment and billing providers;</li>
            <li>postal fulfilment providers;</li>
            <li>analytics and monitoring providers;</li>
            <li>AI and OCR providers;</li>
            <li>customer support tools;</li>
            <li>professional advisers, insurers, auditors, and legal representatives;</li>
            <li>law enforcement, regulators, courts, local authorities, or other competent bodies where required by law; and</li>
            <li>other users within a tenancy or workflow where you or another authorised user has invited, linked, or shared access with them.</li>
          </ul>
          <p><strong>We do not sell personal data to third parties.</strong></p>

          <h2>11. International transfers</h2>
          <p>Some of our service providers may process personal data outside the UK. Where we transfer personal data internationally, we will take reasonable steps to ensure appropriate safeguards are in place, such as adequacy regulations, the UK International Data Transfer Agreement, addendum-backed standard contractual clauses, or other lawful transfer mechanisms.</p>

          <h2>12. Data retention</h2>
          <p>The ICO says privacy notices should explain retention periods or the criteria used to decide them. We retain personal data only for as long as reasonably necessary for the purposes described in this policy, including:</p>
          <ul>
            <li>account and tenancy records: while the account is active and for up to 7 years after closure;</li>
            <li>billing and invoice data: up to 7 years;</li>
            <li>support tickets: up to 3 years after closure;</li>
            <li>analytics data: according to your analytics configuration and minimisation settings;</li>
            <li>marketing preferences and suppression lists: as needed to respect opt-out choices;</li>
            <li>documents and uploads: while needed for the user's account and retention obligations, then securely deleted or anonymised.</li>
          </ul>

          <h2>13. How we protect personal data</h2>
          <p>We use technical and organisational measures designed to protect personal data, including encryption in transit, access controls, role-based permissions, audit logging, provider security controls, environment separation, backup and recovery processes, and session and credential safeguards.</p>
          <p>No system is completely secure, so we cannot guarantee absolute security.</p>

          <h2>14. Your rights</h2>
          <p>Depending on the circumstances, you may have the right to:</p>
          <ul>
            <li>be informed about how your data is used;</li>
            <li>access your personal data;</li>
            <li>correct inaccurate data;</li>
            <li>erase your data;</li>
            <li>restrict processing;</li>
            <li>object to processing;</li>
            <li>data portability; and</li>
            <li>withdraw consent where consent is the lawful basis.</li>
          </ul>
          <p>You also have the right to complain to the Information Commissioner's Office (ICO) if you think your data protection rights have been breached.</p>
          <p>To exercise your rights, contact <a href="mailto:support@actcheck.co.uk">support@actcheck.co.uk</a>.</p>

          <h2>15. Children's data</h2>
          <p>ActCheck is not directed at children as a general audience product. We do not knowingly invite children to create independent legal/compliance accounts.</p>
          <p>However, tenancy or household records may include information about children where users input that information as part of property, occupancy, household, or evidence records. We process that information only as necessary to operate the Platform and associated workflows.</p>

          <h2>16. Third-party links and government sources</h2>
          <p>The Platform may contain links to government, regulator, or third-party sites, such as GOV.UK or the ICO. We are not responsible for the privacy practices of those external sites.</p>

          <h2>17. Changes to this Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. Where changes are material, we will take reasonable steps to notify users, such as by email, in-app notice, or website banner. The updated version will show a new effective date.</p>

          <h2>18. Contact us</h2>
          <p>For privacy questions, rights requests, or complaints, contact:</p>
          <ul>
            <li><strong>ActCheck</strong></li>
            <li>Email: <a href="mailto:support@actcheck.co.uk">support@actcheck.co.uk</a></li>
            <li>Website: <a href="https://actcheck.co.uk">https://actcheck.co.uk</a></li>
            <li>Postal address: Peterborough, England</li>
          </ul>
          <p>If you are unhappy with how we handle your personal data, you can complain to the <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">Information Commissioner's Office</a>.</p>
        </div>
      </div>
    </div>
  );
}
